import { HandledError } from "../models/HandledError";
import { AVBusService } from "./bus.service";
import { ErrorCode } from "../models/enums";
import { Device, isAuviousCoreSupported } from "@auvious/compatibility";

class DeviceServiceCtrl extends Device {
  constructor() {
    super();
  }

  public get isMobile() {
    return Device.isMobile;
  }

  public get isFirefox() {
    return Device.isFirefox;
  }

  public get isIOS() {
    return Device.isiOS;
  }

  public get isAndroid(){
    return Device.isAndroid;
  }

  public get isSupportedBrowser(){
    return isAuviousCoreSupported();
  }

  public copyTextToClipboard(text) {
    const textArea = document.createElement("textarea");

    //
    // *** This styling is an extra step which is likely not required. ***
    //
    // Why is it here? To ensure:
    // 1. the element is able to have focus and selection.
    // 2. if element was to flash render it has minimal visual impact.
    // 3. less flakyness with selection and copying which **might** occur if
    //    the textarea element is not visible.
    //
    // The likelihood is the element won't even render, not even a flash,
    // so some of these are just precautions. However in IE the element
    // is visible whilst the popup box asking the user for permission for
    // the web page to copy to the clipboard.
    //

    // Place in top-left corner of screen regardless of scroll position.
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";

    // Ensure it has a small width and height. Setting to 1px / 1em
    // doesn't work as this gives a negative w/h on some browsers.
    textArea.style.width = "2em";
    textArea.style.height = "2em";

    // We don't need padding, reducing the size if it does flash render.
    textArea.style.padding = "0";

    // Clean up any borders.
    textArea.style.border = "none";
    textArea.style.outline = "none";
    textArea.style.boxShadow = "none";

    // Avoid flash of white box if rendered for any reason.
    textArea.style.background = "transparent";

    textArea.value = text;

    document.body.appendChild(textArea);

    textArea.select();

    let success = false;

    try {
      success = document.execCommand("copy");
      // const msg = successful ? 'successful' : 'unsuccessful';
      // debug('Copying text command was ' + msg);
    } catch (ex) {
      AVBusService.errorLog(
        new HandledError(ex, ErrorCode.COPY_TO_CLIPBOARD_FAILED)
      );
      // debug('Oops, unable to copy');
    }

    document.body.removeChild(textArea);
    return success;
  }

  public get supportsDisplayCapture() {
    // @ts-ignore
    return !!navigator.mediaDevices?.getDisplayMedia || !!navigator.getDisplayMedia;
  }

  public getUserMediaSafe(constraints): Promise<MediaStream> {
    if (
      !navigator.mediaDevices ||
      (!!navigator.mediaDevices &&
        navigator.mediaDevices.getUserMedia === undefined)
    ) {
      // First get ahold of the legacy getUserMedia, if present
      const getUserMediaC =
        navigator["getUserMedia"] ||
        navigator["webkitGetUserMedia"] ||
        navigator["mozGetUserMedia"];
      // Some browsers just don't implement it - return a rejected promise with an error
      // to keep a consistent interface
      if (!getUserMediaC) {
        return Promise.reject(
          new Error("getUserMedia is not implemented in this browser")
        );
      }
      // Otherwise, wrap the call to the old navigator.getUserMedia with a Promise
      return new Promise(function (resolve, reject) {
        // getUserMediaC.call(navigator, constraints, resolve, reject);
        getUserMediaC(constraints, resolve, reject);
      });
    }
    return navigator.mediaDevices.getUserMedia(constraints);
  }

  public async enumerateDevicesSafe(): Promise<MediaDeviceInfo[]> {
    if (!navigator.mediaDevices?.enumerateDevices) {
      throw new Error("enumerateDevices() not supported.");
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices;
    } catch (ex) {
      throw new Error("enumerateDevices() not supported.");
    }
  }

  public closeMediaStream(stream: MediaStream) {
    try {
      if (!!stream.getTracks) {
        stream.getTracks().forEach((track) => {
          track.stop();
        });
      } else {
        stream.getAudioTracks().forEach((track) => {
          track.stop();
        });
        stream.getVideoTracks().forEach((track) => {
          track.stop();
        });
      }
    } catch (ex) {
      if (
        !!stream &&
        "stop" in stream &&
        typeof stream["stop"] === "function"
      ) {
        // @ts-ignore
        stream.stop();
      }
    }
  }
}

export const DeviceService = new DeviceServiceCtrl();
